import { t as uiText } from '../../i18n';
// src/components/auth/OpenExternallyDialog.tsx
import React, { useMemo } from 'react';
import '../../pages/Login.css';

// CSSアニメーションの定義
const modalStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes zoomIn {
    from { 
      opacity: 0;
      transform: scale(0.95);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// スタイルをDOMに追加
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = modalStyles;
  if (!document.head.querySelector('style[data-modal-animations]')) {
    styleElement.setAttribute('data-modal-animations', 'true');
    document.head.appendChild(styleElement);
  }
}

export interface OpenExternallyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appName?: string; // 表示名（デフォルト: notreu）
  targetUrl?: string; // コピー/外部起動対象（未指定なら /sys/login）
}

const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';

const isIOS = () => hasNavigator && /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroid = () => hasNavigator && /Android/i.test(navigator.userAgent);

export const OpenExternallyDialog: React.FC<OpenExternallyDialogProps> = ({
  isOpen,
  onClose,
  appName = 'notreu',
  targetUrl,
}) => {
  const origin = useMemo(() => (hasWindow ? window.location.origin : ''), []);
  const effectiveUrl = useMemo(() => {
    if (!hasWindow) return '';
    if (targetUrl && typeof targetUrl === 'string' && targetUrl.trim()) {
      return targetUrl.trim();
    }
    return origin ? `${origin}/sys/login` : '';
  }, [origin, targetUrl]);

  // Android Chromeのintentリンク（100%ではないが脱出率を少し上げる）
  const androidIntentUrl = useMemo(() => {
    if (!hasWindow) return '';
    try {
      const u = new URL(effectiveUrl);
      // intent://host/path#Intent;scheme=https;package=com.android.chrome;end
      return `intent://${u.host}${u.pathname}${u.search}${u.hash}#Intent;scheme=${u.protocol.replace(':', '')};package=com.android.chrome;end`;
    } catch {
      return '';
    }
  }, [effectiveUrl]);

  // ブラウザアプリで直接開く試行
  const handleOpenInBrowser = async () => {
    try {
      // Android: Chromeで開く試行
      if (isAndroid()) {
        // まずwindow.openを試行
        const opened = window.open(effectiveUrl, '_blank');
        if (!opened) {
          // 失敗時はintentリンクを試行
          if (androidIntentUrl) {
            window.location.href = androidIntentUrl;
          } else {
            // 最終フォールバック
            window.location.href = effectiveUrl;
          }
        }
        return;
      }

      // iOS: Safariで開く
      if (isIOS()) {
        // In-Appブラウザからは直接Safariを開けないため、コピー機能にフォールバック
        handleCopy();
        return;
      }

      // その他の場合: デフォルトブラウザで開く試行
      const opened = window.open(effectiveUrl, '_blank');
      if (!opened) {
        // ポップアップブロックされた場合はフォールバック
        handleCopy();
      }
    } catch (error) {
      console.warn('ブラウザアプリでの直接起動に失敗:', error);
      // 失敗時はコピーにフォールバック
      handleCopy();
    }
  };

  const handleCopy = async () => {
    try {
      if (hasNavigator && navigator.clipboard) {
        await navigator.clipboard.writeText(effectiveUrl);
        alert(uiText("Link copied. Please open it in Safari or Chrome."));
      } else {
        alert(uiText("Please open the following URL in Safari or Chrome:\n{{effectiveUrl}}", { effectiveUrl: effectiveUrl }));
      }
    } catch {
      alert(uiText("Please open the following URL in Safari or Chrome:\n{{effectiveUrl}}", { effectiveUrl: effectiveUrl }));
    }
  };

  if (!isOpen) return null;

  // ポップアップとして表示（画面の中央に前面表示）
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        animation: 'fadeIn 0.2s ease-out',
        zIndex: 999999,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div className="login-content" style={{ 
        animation: 'zoomIn 0.2s ease-out', 
        width: '300px', 
        overflow: 'hidden',
        backgroundColor: '#fbf8f6',
        borderRadius: '10px'
      }}>
        <div className="mb-6">
          <h2 className="login-title" style={{ textAlign: 'left', marginBottom: '20px', fontSize: '18px' }}>{uiText("Some features are limited in in-app browsers. Please open this in an external browser.")}</h2>
        </div>
        <p className="mb-6 text-gray-600 leading-relaxed" style={{ textAlign: 'left' }}>
          {uiText("Google sign-in is restricted in the in-app browser (In-App Browser) or WebView you are using, so this is unavailable. Share or copy the link with the button below, and")}
          <span className="font-semibold text-gray-800">{isIOS() ? uiText(" Safari on iPhone ") : isAndroid() ? uiText(" Chrome on Android ") : ' Safari/Chrome '}</span>
          {uiText(" to open it.")}
        </p>

        <div className="mb-6">
          <input
            className="focus:outline-none"
            style={{ 
              backgroundColor: 'var(--workspace-border-soft, #f5f0eb)', 
              border: 'none', 
              borderRadius: '8px',
              padding: '16px',
              fontSize: '16px',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              width: '100%',
              boxSizing: 'border-box'
            }}
            value={effectiveUrl}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>

        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={handleCopy}
            style={{ 
              margin: '2px', 
              backgroundColor: 'var(--workspace-primary, #cfae91)', 
              color: 'white', 
              border: 'none',
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              minWidth: '200px'
            }}
          >
            {uiText("Copy")}
          </button>
        </div>

        <div className="rounded-lg bg-gray-100 p-4 text-sm leading-relaxed text-gray-600" style={{ textAlign: 'left' }}>
          <div className="font-semibold mb-2 text-gray-800">{uiText("Tips")}</div>
          <div className="space-y-1">
                <div>{uiText("From the \"⋮\" icon, tap \"Open in browser\".")}</div>
                <div>{uiText("Or copy the URL with the copy button and open it again in your browser.")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

